class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', () => FacetFiltersForm.updateResetButtonsState());

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    FacetFiltersForm.updateResetButtonsState();
  }

  static updateResetButtonsState() {
    const checkedFilters = document.querySelectorAll(
      'facet-filters-form input[type="checkbox"][name^="filter."]:checked',
    );
    const priceInputs = document.querySelectorAll(
      'facet-filters-form input[name="filter.v.price.gte"], facet-filters-form input[name="filter.v.price.lte"]',
    );
    let priceActive = false;
    priceInputs.forEach((input) => {
      if (input.value && input.value.trim() !== '') priceActive = true;
    });
    const hasActive = checkedFilters.length > 0 || priceActive;

    document.querySelectorAll('.mobile-facets__clear, .active-facets__button-remove').forEach((el) => {
      el.classList.toggle('disabled', !hasActive);
      if (hasActive) {
        el.removeAttribute('aria-disabled');
        if (el.tagName === 'A') el.removeAttribute('tabindex');
      } else {
        el.setAttribute('aria-disabled', 'true');
        if (el.tagName === 'A') el.setAttribute('tabindex', '-1');
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Ahead of the renders: the date-aware count probe builds its requests from
    // window.location.search, and renderSectionFromCache runs synchronously, so a hash
    // written afterwards would leave cached re-renders counting the previous selection.
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.updateDateFilteredFacetAvailability();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateDateFilteredFacetAvailability();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  // The query one value's count is measured with. Every value in a group shares a single param
  // name, so an OR group's other picks come out before the probed value goes in - each value is
  // then counted on its own against the *other* groups' selections, which is what Shopify's own
  // counts mean. An AND group narrows with each pick, so there its siblings stay. Sorted, so the
  // same combination always produces the same cache key.
  static dateFacetProbeQuery(input) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    const operator = input.closest('.js-filter')?.dataset.filterOperator;
    if (operator !== 'AND') params.delete(input.name);
    if (!params.getAll(input.name).includes(input.value)) params.append(input.name, input.value);
    params.sort();
    return params.toString();
  }

  static applyDateFilteredFacetCount(name, value, availableCount) {
    const matchingInputs = document.querySelectorAll(
      `facet-filters-form input[type="checkbox"][name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`,
    );
    matchingInputs.forEach((matchingInput) => {
      // An applied value keeps its row even at zero, so the shopper can still clear it.
      const unavailable = availableCount === 0 && !matchingInput.checked;
      matchingInput.dataset.dateAvailabilityChecked = 'true';
      matchingInput.disabled = unavailable;
      const label = matchingInput.closest('label');
      label?.classList.toggle('disabled', unavailable);
      matchingInput.closest('li')?.toggleAttribute('hidden', unavailable);
      label?.querySelector('[data-facet-count]')?.replaceChildren(String(availableCount));
      const announcement = label?.querySelector('[data-facet-count-announcement]');
      if (announcement) {
        announcement.textContent = announcement.textContent.replace(/\(\s*\d+\s*/, `(${availableCount} `);
      }
    });
  }

  static async updateDateFilteredFacetAvailability() {
    const facetContainer = document.querySelector('.facets-container[data-date-filtered="true"]');
    if (!facetContainer) return;

    FacetFiltersForm.updateLastVisibleFacetItems();

    const sectionId = document.getElementById('product-grid')?.dataset.id;
    if (!sectionId) return;

    const requestId = (FacetFiltersForm.dateFacetRequestId || 0) + 1;
    FacetFiltersForm.dateFacetRequestId = requestId;
    document
      .querySelectorAll(
        'facet-filters-form input[type="checkbox"][name^="filter."]:not([hidden]):not(:checked):disabled',
      )
      .forEach((input) => {
        input.closest('li')?.setAttribute('hidden', 'hidden');
      });
    const inputs = Array.from(
      document.querySelectorAll('facet-filters-form input[type="checkbox"][name^="filter."]:not([hidden])'),
    ).filter(
      (input) =>
        input.value && (input.checked || !input.disabled) && input.dataset.dateAvailabilityChecked !== 'true',
    );

    // Counts already measured for this exact query are painted now, in the same task as the
    // render. Without that the server's own figure - which still counts the products the grid
    // drops as past-dated - stays on screen until the probe answers, and the number visibly
    // jumps. Toggling a value never changes its own group's queries, so those rows come
    // straight back from here. Keying by query also collapses the mobile and desktop copies of
    // a value into one request.
    const pending = new Map();
    inputs.forEach((input) => {
      const query = FacetFiltersForm.dateFacetProbeQuery(input);
      const cached = FacetFiltersForm.dateFacetCounts.get(query);
      if (cached === undefined) {
        // Several values can share one query - the lone pick of two different OR groups each
        // probe the unmodified query, since dropping a group of one and re-adding it is a
        // no-op. They measure the same figure, so they share the request but all get painted.
        const waiting = pending.get(query);
        if (waiting) {
          if (!waiting.some(({ name, value }) => name === input.name && value === input.value)) {
            waiting.push({ name: input.name, value: input.value });
          }
        } else {
          pending.set(query, [{ name: input.name, value: input.value }]);
        }
        return;
      }
      FacetFiltersForm.applyDateFilteredFacetCount(input.name, input.value, cached);
    });
    FacetFiltersForm.updateLastVisibleFacetItems();

    const countForQuery = async (query) => {
      let url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&${query}`;
      let pagesChecked = 0;
      let availableCount = 0;

      while (url && pagesChecked < 50) {
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) return null;

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = documentFragment.querySelector('#product-grid');
        const visibleItems = grid?.querySelectorAll('.collection-grid__item:not(.collection-grid__item--promo-banner)');
        const visibleCount = visibleItems?.length || 0;
        availableCount += visibleCount;

        const nextPage = documentFragment.querySelector('load-more-pagination')?.dataset.nextUrl;
        if (!nextPage) return availableCount;

        url = `${nextPage}${nextPage.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`;
        pagesChecked += 1;
      }

      // Cap reached with pages still to walk: the total is short, so report no answer rather
      // than cache an undercount the page would then repeat for the rest of the session.
      return null;
    };

    await Promise.all(
      Array.from(pending, async ([query, values]) => {
        try {
          const availableCount = await countForQuery(query);
          if (availableCount === null) return;
          // Cached whoever asked: the figure describes the query, not the render that wanted it.
          FacetFiltersForm.dateFacetCounts.set(query, availableCount);
          if (requestId !== FacetFiltersForm.dateFacetRequestId) return;

          values.forEach(({ name, value }) => {
            FacetFiltersForm.applyDateFilteredFacetCount(name, value, availableCount);
          });
          FacetFiltersForm.updateLastVisibleFacetItems();
        } catch (error) {
          // A failed availability probe must leave the native Shopify option usable.
          console.warn('Unable to check date-filtered facet availability', error);
        }
      }),
    );
  }

  static updateLastVisibleFacetItems() {
    document.querySelectorAll('.mobile-facets__list').forEach((list) => {
      list.querySelectorAll('.mobile-facets__item.is-last-visible').forEach((item) => {
        item.classList.remove('is-last-visible');
      });
      const visibleItems = list.querySelectorAll('.mobile-facets__item:not([hidden])');
      visibleItems[visibleItems.length - 1]?.classList.add('is-last-visible');
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    // A group inserted by this render carries `open` from the server but not the class the
    // arrow's direction is drawn from. One mid-close is skipped: it drops the class up front
    // but keeps `open` until the height transition ends.
    document.querySelectorAll('.mobile-facets__details[open]').forEach((details) => {
      if (details.querySelector('.mobile-facets__submenu[data-animating]')) return;
      details.classList.add('is-expanded');
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.updateResetButtonsState();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    if (!(form instanceof HTMLFormElement)) return '';
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const targetForm = event.target.closest('form');
    if (!targetForm) return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const sourceClassName = (event.target && event.target.className) || '';
    if (typeof sourceClassName === 'string' && sourceClassName.includes('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(targetForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = targetForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.dateFacetCounts = new Map();
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.updateDateFilteredFacetAvailability();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

(function setupSmoothMobileFacets() {
  const ANIMATING_ATTR = 'data-animating';

  function animateOpen(details, summary, submenu) {
    details.setAttribute('open', '');
    details.classList.add('is-expanded');
    summary.setAttribute('aria-expanded', 'true');
    submenu.setAttribute(ANIMATING_ATTR, 'open');
    submenu.style.height = '0px';
    requestAnimationFrame(() => {
      submenu.style.height = submenu.scrollHeight + 'px';
    });
    const onEnd = (e) => {
      if (e.propertyName !== 'height' || e.target !== submenu) return;
      submenu.removeEventListener('transitionend', onEnd);
      submenu.style.height = '';
      submenu.removeAttribute(ANIMATING_ATTR);
    };
    submenu.addEventListener('transitionend', onEnd);
  }

  function animateClose(details, summary, submenu) {
    submenu.setAttribute(ANIMATING_ATTR, 'close');
    details.classList.remove('is-expanded');
    summary.setAttribute('aria-expanded', 'false');
    submenu.style.height = submenu.scrollHeight + 'px';
    submenu.offsetHeight; // force reflow
    submenu.style.height = '0px';
    const onEnd = (e) => {
      if (e.propertyName !== 'height' || e.target !== submenu) return;
      submenu.removeEventListener('transitionend', onEnd);
      details.removeAttribute('open');
      submenu.style.height = '';
      submenu.removeAttribute(ANIMATING_ATTR);
    };
    submenu.addEventListener('transitionend', onEnd);
  }

  function syncInitialState() {
    document.querySelectorAll('.mobile-facets__details[open]').forEach((details) => {
      details.classList.add('is-expanded');
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncInitialState);
  } else {
    syncInitialState();
  }

  document.addEventListener(
    'click',
    (e) => {
      const summary = e.target.closest('.mobile-facets__summary');
      if (!summary) return;
      const details = summary.closest('.mobile-facets__details');
      if (!details) return;
      const submenu = details.querySelector('.mobile-facets__submenu');
      if (!submenu) return;

      e.preventDefault();
      e.stopPropagation();

      if (submenu.hasAttribute(ANIMATING_ATTR)) return;
      if (details.hasAttribute('open')) animateClose(details, summary, submenu);
      else animateOpen(details, summary, submenu);
    },
    true,
  );
})();
